import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, Loader2, CheckCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';

const API = API_URL;

export const LibraryFilters = ({
  // Estados
  showFavoritesOnly,
  setShowFavoritesOnly,
  showOnlyDenonPendientes,
  setShowOnlyDenonPendientes,
  denonPendientesCount,
  filterRating,
  setFilterRating,
  filterEnergia,
  setFilterEnergia,
  filterMomento,
  setFilterMomento,
  filterGenre,
  setFilterGenre,
  filterDecade,
  setFilterDecade,
  hasActiveFilters,
  clearAllFilters,
  fetchDenonPendientesCount,
  // Sorting
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  // Datos
  MOMENTOS_BODA,
  availableGenres
}) => {
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [clearingPendientes, setClearingPendientes] = useState(false);

  const handleCreateDenonPlaylist = async () => {
    if (denonPendientesCount === 0) {
      toast.error('No hay canciones pendientes para crear la playlist');
      return;
    }

    try {
      setCreatingPlaylist(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/songs/create-denon-playlist`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(
          <div className="space-y-2">
            <p>✅ {response.data.message}</p>
            <a 
              href={response.data.playlist_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-400 underline hover:text-emerald-300"
            >
              🎵 Abrir playlist en Spotify
            </a>
          </div>,
          { duration: 10000 }
        );
        // Refresh the count (should be 0 now)
        fetchDenonPendientesCount();
        // Turn off the filter since there are no more pending songs
        setShowOnlyDenonPendientes(false);
      }
    } catch (error) {
      console.error('Error creating Denon playlist:', error);
      const errorMsg = error.response?.data?.detail || 'Error al crear la playlist';
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setCreatingPlaylist(false);
    }
  };

  const handleClearDenonPendientes = async () => {
    if (denonPendientesCount === 0) {
      toast.info('No hay canciones pendientes para desmarcar');
      return;
    }

    if (!window.confirm(`¿Desmarcar ${denonPendientesCount} canciones como pendientes?\n\nUsa esto después de haberlas pasado a Denon Engine.`)) {
      return;
    }

    try {
      setClearingPendientes(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/songs/clear-denon-pendientes`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        // Refresh the count
        fetchDenonPendientesCount();
        // Turn off the filter
        setShowOnlyDenonPendientes(false);
      }
    } catch (error) {
      console.error('Error clearing denon pendientes:', error);
      const errorMsg = error.response?.data?.detail || 'Error al desmarcar pendientes';
      toast.error(errorMsg);
    } finally {
      setClearingPendientes(false);
    }
  };

  return (
    <div className="mb-4 space-y-2">
      {/* Fila 1: Favoritos y Denon */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          className={`${showFavoritesOnly ? 'bg-pink-600 hover:bg-pink-700' : 'bg-slate-700 hover:bg-slate-600'} text-xs`}
          size="sm"
        >
          <Heart className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-white' : ''}`} />
          {showFavoritesOnly ? 'Favoritos' : 'Solo Favoritos'}
        </Button>
        
        <Button
          onClick={() => {
            setShowOnlyDenonPendientes(!showOnlyDenonPendientes);
            if (!showOnlyDenonPendientes) fetchDenonPendientesCount();
          }}
          className={`${showOnlyDenonPendientes ? 'bg-orange-600 hover:bg-orange-700' : 'bg-slate-700 hover:bg-slate-600'} text-xs`}
          size="sm"
        >
          🎧 {showOnlyDenonPendientes ? `Denon (${denonPendientesCount})` : `Pendientes${denonPendientesCount > 0 ? ` (${denonPendientesCount})` : ''}`}
        </Button>
      </div>

      {/* Botón para crear playlist de Spotify cuando hay pendientes */}
      {showOnlyDenonPendientes && denonPendientesCount > 0 && (
        <div className="space-y-2">
          <Button
            onClick={handleCreateDenonPlaylist}
            disabled={creatingPlaylist}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            size="sm"
          >
            {creatingPlaylist ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creando playlist...
              </>
            ) : (
              <>
                🎵 Crear Playlist Spotify ({denonPendientesCount} canciones)
              </>
            )}
          </Button>
          
          <Button
            onClick={handleClearDenonPendientes}
            disabled={clearingPendientes}
            className="w-full bg-slate-600 hover:bg-slate-500 text-white text-xs"
            size="sm"
            data-testid="clear-denon-pendientes-btn"
          >
            {clearingPendientes ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Desmarcando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Ya pasadas a Denon - Desmarcar todas
              </>
            )}
          </Button>
        </div>
      )}

      {/* Fila 2: Valoraciones y Energía */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="">Valoraciones</option>
          <option value="__sin_valoracion__">⚠️ Sin valoración</option>
          {[5, 4, 3, 2, 1].map((rating) => (
            <option key={rating} value={rating.toString()}>
              {rating} ⭐
            </option>
          ))}
        </select>

        <select
          value={filterEnergia}
          onChange={(e) => setFilterEnergia(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="">Energías</option>
          <option value="Baja">⚡ Baja</option>
          <option value="Media">⚡⚡ Media</option>
          <option value="Alta">⚡⚡⚡ Alta</option>
          <option value="Muy Alta">⚡⚡⚡⚡ Muy Alta</option>
        </select>
      </div>

      {/* Fila 3: Momentos y Estilos */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={filterMomento}
          onChange={(e) => setFilterMomento(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="">Momentos</option>
          <option value="__sin_momento__">⚠️ Sin momento asignado</option>
          {MOMENTOS_BODA.map((momento) => (
            <option key={momento} value={momento}>{momento}</option>
          ))}
        </select>

        <select
          value={filterGenre}
          onChange={(e) => setFilterGenre(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="">Estilos</option>
          <option value="__sin_estilo__">⚠️ Sin estilo asignado</option>
          {availableGenres.map((genre) => (
            <option key={genre} value={genre}>{genre}</option>
          ))}
        </select>
      </div>

      {/* Fila 4: Filtro por Década */}
      <div className="grid grid-cols-1 gap-2">
        <select
          value={filterDecade}
          onChange={(e) => setFilterDecade(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="all">📅 Todas las Décadas</option>
          <option value="1960">🎵 1960s</option>
          <option value="1970">🎸 1970s</option>
          <option value="1980">🎹 1980s</option>
          <option value="1990">💿 1990s</option>
          <option value="2000">🎧 2000s</option>
          <option value="2010">📱 2010s</option>
          <option value="2020">🎤 2020s</option>
        </select>
      </div>

      {/* Fila 5: Ordenación */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white rounded px-2 py-2 text-xs"
        >
          <option value="title">📝 Ordenar por Título</option>
          <option value="artist">🎤 Ordenar por Artista</option>
          <option value="created_at">📥 Fecha de Añadido</option>
          <option value="updated_at">✏️ Última Modificación</option>
          <option value="bpm">🥁 BPM</option>
          <option value="year">📅 Año</option>
        </select>
        
        <Button
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="bg-slate-700 hover:bg-slate-600 text-xs"
          size="sm"
        >
          {sortOrder === 'asc' ? (
            <>
              <ArrowUp className="w-4 h-4 mr-1" />
              Ascendente
            </>
          ) : (
            <>
              <ArrowDown className="w-4 h-4 mr-1" />
              Descendente
            </>
          )}
        </Button>
      </div>

      {/* Acceso rápido: Últimas añadidas */}
      {sortBy !== 'created_at' && (
        <Button
          onClick={() => {
            setSortBy('created_at');
            setSortOrder('desc');
          }}
          className="w-full bg-cyan-700 hover:bg-cyan-600 text-white text-xs"
          size="sm"
        >
          🆕 Ver Últimas Canciones Añadidas
        </Button>
      )}

      {hasActiveFilters && (
        <Button
          onClick={clearAllFilters}
          variant="outline"
          className="w-full bg-slate-700 border-slate-600 text-white hover:bg-slate-600 text-xs"
          size="sm"
        >
          Limpiar Filtros
        </Button>
      )}
    </div>
  );
};
